import * as React from "react";
import { Grid } from "@mui/material";
import { Formik } from "formik";
import { useMemo, useState } from "react";
import * as yup from "yup";
import { v4 as uuidv4 } from 'uuid';
import { ADMIN_FORM_MODES } from "../../../utils/constants";
import OZDialog from "../../../components/admin/common/OZDialog";
import CustomAutoComplete from "../../../components/admin/common/AutoComplete/CustomAutoComplete";
import OZSelect from "../../../components/admin/common/OZSelect";
import OZInput from "../../../components/admin/common/OZInput";
import { Typography, Box, IconButton, Button, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { showErrorNotification } from "../../../components/admin/common/NotiService";

const refreshIntervalOptions = [0, 5, 10, 15, 20, 30, 60, 120, 180, 240, 300];

const AssignWidgetsForm = (assignFormProps) => {
    let assignFormInitialMode = assignFormProps.data ? ADMIN_FORM_MODES.edit : ADMIN_FORM_MODES.add;
    assignFormInitialMode = assignFormProps?.openDialog === true ? ADMIN_FORM_MODES.edit : assignFormInitialMode;
    const [openAddDialog, setOpenAddDialog] = useState(true);
    const [assignFormMode] = useState(assignFormInitialMode);
    const users = useMemo(() => assignFormProps.users, [assignFormProps.users]);
    const widgets = useMemo(() => assignFormProps.widgets, [assignFormProps.widgets]);

    const closeAssignForm = () => {
        assignFormProps.onClose();
        setOpenAddDialog(false);
    };

    return (
        <div>
            {assignFormMode === ADMIN_FORM_MODES.add ? (
                <OZDialog
                    title="Assign Widgets to User"
                    maxWidth='2xl'
                    saveButtonProps={{
                        form: 'assignWidgetForm',
                        type: 'submit'
                    }}
                    open={openAddDialog}
                    mode={assignFormMode}
                    onClose={closeAssignForm}
                >
                    <DataForm
                        widgets={widgets}
                        users={users}
                        assignFormMode={assignFormMode}
                        addAssignment={assignFormProps.addAssignment}
                        closeAssignForm={closeAssignForm}
                    />
                </OZDialog>
            ) : (
                <OZDialog
                    title="Edit Widget Assignment"
                    saveButtonProps={{
                        form: 'assignWidgetForm',
                        type: 'submit'
                    }}
                    open={openAddDialog}
                    mode={assignFormMode}
                    onClose={closeAssignForm}
                    hideSaveButton={!(assignFormProps.editAccess) || false}
                >
                    <DataForm
                        editData={assignFormProps.data}
                        widgets={widgets}
                        users={users}
                        assignFormMode={assignFormMode}
                        addAssignment={assignFormProps.addAssignment}
                        closeAssignForm={closeAssignForm}
                    />
                </OZDialog>
            )}
        </div>
    );
};

function DataForm({ editData, widgets, users, assignFormMode, addAssignment, closeAssignForm }) {

    const validationSchema = useMemo(
        () =>
            yup.object({
                selectedUser: yup
                    .object()
                    .nullable()
                    .required("User selection is required"),
                widgetSelection: yup
                    .array()
                    .min(1, "Please select at least one widget"),
                assignedWidgets: yup
                    .array()
                    .of(yup.object().shape({
                        widget: yup.object().required(),
                        refreshInterval: yup.number().oneOf([0, 5, 10, 15, 20, 30, 60, 120, 180, 240, 300]),
                        defaultParams: yup.array().nullable()
                    }))
                    .min(1, "At least one widget must be assigned with properties configured")
            }),
        []
    );

    let assignFormInitialData = editData
        ? {
            ...editData,
            selectedUser: editData.selectedUser || null,
            assignedWidgets: editData.assignedWidgets ?
                editData.assignedWidgets.map(assignedWidget => {
                    let defaultParams = [];
                    try {
                        // Handle the new assignment structure with userWidgetParams
                        const params = assignedWidget.userWidgetParams || assignedWidget.defaultParams || assignedWidget.widgetParams;
                        defaultParams = typeof params === 'string' ?
                            JSON.parse(params || '{}') :
                            (params || {});

                        // Convert object to array format for the form
                        if (defaultParams && typeof defaultParams === 'object' && !Array.isArray(defaultParams)) {
                            defaultParams = Object.keys(defaultParams).map(key => ({
                                key: key,
                                value: defaultParams[key],
                                id: uuidv4()
                            }));
                        } else if (!Array.isArray(defaultParams)) {
                            defaultParams = [];
                        }
                    } catch (error) {
                        console.error("Error parsing widget parameters:", error);
                        defaultParams = [];
                    }

                    // For the new structure, assignedWidget has id, userRefreshInterval, userWidgetParams
                    const widget = widgets.find(w => w.id === assignedWidget.id);

                    return {
                        widget,
                        refreshInterval: assignedWidget.userRefreshInterval || 0,
                        defaultParams
                    };
                }).filter(item => item.widget) : [],
            widgetSelection: editData.assignedWidgets ?
                editData.assignedWidgets.map(assignedWidget => {
                    const widget = widgets.find(w => w.id === assignedWidget.id);
                    return widget;
                }).filter(widget => widget) : []
        }
        : {
            selectedUser: null,
            assignedWidgets: [],
            widgetSelection: []
        };

    return (
        <div className="ozAssignWidgetAddForm">
            <Formik
                initialValues={assignFormInitialData}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={(userEnteredValues) => {
                    const formValues = {
                        userId: userEnteredValues.selectedUser?.id || null,
                        widgets: userEnteredValues.assignedWidgets?.map(item => {
                            // Convert array format back to object format
                            const paramsObject = {};
                            if (item.defaultParams && Array.isArray(item.defaultParams)) {
                                item.defaultParams.forEach(param => {
                                    if (param.key && param.value !== undefined) {
                                        paramsObject[param.key] = param.value;
                                    }
                                });
                            }

                            return {
                                id: item.widget.id,
                                userRefreshInterval: item.refreshInterval,
                                userWidgetParams: JSON.stringify(paramsObject)
                            };
                        }) || []
                    };

                    // For updates, include the assignment ID
                    if (assignFormMode === "Edit" && userEnteredValues.id) {
                        formValues.id = userEnteredValues.id;
                    }
                    console.log(formValues);

                    if (assignFormMode === "Create") {
                        addAssignment(formValues, closeAssignForm, "create");
                    } else {
                        addAssignment(formValues, closeAssignForm, "update");
                    }
                }}
            >
                {(formik) => (
                    <div>
                        <form
                            onSubmit={formik.handleSubmit}
                            id="assignWidgetForm"
                            className="ozcwScrollbar oz-assign-widget-form-container"
                        >
                            <Grid container={true} spacing={2}>
                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <CustomAutoComplete
                                        disabled={assignFormMode === "Edit"}
                                        mode='Add'
                                        name="selectedUser"
                                        labelHeader='Select User *'
                                        placeholder={"Select a user"}
                                        Datas={users}
                                        optionLabelName='name'
                                        multiple={false}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography
                                        fontSize='12px'
                                        fontWeight={400}
                                        lineHeight='14px'
                                        marginBottom={'6px'}
                                        marginLeft='1px'
                                        color="#212121"
                                    >
                                        Select Widgets to Assign *
                                    </Typography>
                                    <CustomAutoComplete
                                        mode='Add'
                                        name="widgetSelection"
                                        placeholder={"Select widgets"}
                                        Datas={widgets}
                                        optionLabelName='code'
                                        multiple={true}
                                        showCheckBox={true}
                                        showselectall="true"
                                        OnSaveCallBack={(data) => {
                                            try {
                                                const currentAssigned = formik.values.assignedWidgets || [];
                                                const newAssigned = data.value ? data.value.map(widget => {
                                                    const existing = currentAssigned.find(item => item.widget?.code === widget?.code);
                                                    return existing || {
                                                        widget,
                                                        refreshInterval: widget?.userRefreshInterval,
                                                        defaultParams: widget?.userWidgetParams
                                                    };
                                                }) : [];

                                                formik.setFieldValue('assignedWidgets', newAssigned);
                                            } catch (error) {
                                                console.error("Error in OnSaveCallBack", error);
                                                formik.setFieldValue('assignedWidgets', []);
                                            }
                                        }}
                                    />
                                </Grid>

                                {/* Selected Widgets with All Property Overrides */}
                                {formik.values.assignedWidgets && formik.values.assignedWidgets.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography
                                            fontSize='12px'
                                            fontWeight={400}
                                            lineHeight='14px'
                                            marginBottom={'12px'}
                                            marginLeft='1px'
                                            color="#212121"
                                        >
                                            Configure Widget Overrides for Each Assignment
                                        </Typography>
                                        <Box sx={{
                                            border: '1px solid #E6E5E6',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            backgroundColor: '#FAFAFA'
                                        }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '11px',
                                                    color: '#666',
                                                    marginBottom: '16px'
                                                }}
                                            >
                                                Configure refresh intervals and parameters for each selected widget. Changes are automatically saved as you modify selections above.
                                            </Typography>
                                            {formik.values.assignedWidgets.map((item, index) => (
                                                <Accordion key={item.widget?.id || index} sx={{
                                                    marginBottom: '12px',
                                                    '&:before': { display: 'none' },
                                                    boxShadow: 'none',
                                                    border: '1px solid #E0E0E0',
                                                    borderRadius: '8px !important',
                                                    overflow: 'hidden',
                                                    '&.Mui-expanded': {
                                                        borderRadius: '8px !important'
                                                    },
                                                    '& .MuiAccordionSummary-root': {
                                                        borderRadius: '8px'
                                                    },
                                                    '&.Mui-expanded .MuiAccordionSummary-root': {
                                                        borderRadius: '8px 8px 0 0 !important'
                                                    },
                                                    '& .MuiAccordionDetails-root': {
                                                        borderRadius: '0 0 8px 8px'
                                                    }
                                                }}>
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        sx={{
                                                            backgroundColor: '#FFFFFF',
                                                            minHeight: '48px !important',
                                                            '& .MuiAccordionSummary-content': {
                                                                margin: '8px 0 !important',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between'
                                                            }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                                {item.widget?.name || 'Unnamed Widget'}
                                                            </Typography>
                                                        </Box>
                                                        <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const widgetToRemove = formik.values.assignedWidgets[index];

                                                                // Remove from assigned widgets
                                                                const newAssigned = formik.values.assignedWidgets.filter((_, i) => i !== index);
                                                                formik.setFieldValue('assignedWidgets', newAssigned);

                                                                // Also remove from widget selection to keep them in sync
                                                                const newSelection = (formik.values.widgetSelection || []).filter(
                                                                    widget => widget.id !== widgetToRemove.widget?.id
                                                                );
                                                                formik.setFieldValue('widgetSelection', newSelection);
                                                            }}
                                                            sx={{
                                                                color: '#FF4444',
                                                                '&:hover': {
                                                                    backgroundColor: '#FFE6E6'
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </AccordionSummary>
                                                    <AccordionDetails sx={{
                                                        backgroundColor: '#FAFAFA',
                                                        borderTop: '1px solid #E0E0E0'
                                                    }}>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={12} sm={6}>
                                                                <OZSelect
                                                                    name={`assignedWidgets[${index}].refreshInterval`}
                                                                    label="Refresh Interval (seconds) *"
                                                                    options={refreshIntervalOptions}
                                                                    placeholder="Select refresh interval"
                                                                    value={item.refreshInterval}
                                                                    onChange={(e) => {
                                                                        const newAssigned = [...formik.values.assignedWidgets];
                                                                        newAssigned[index].refreshInterval = Number(e.target.value);
                                                                        formik.setFieldValue('assignedWidgets', newAssigned);
                                                                    }}
                                                                />
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '10px',
                                                                        color: '#666',
                                                                        marginTop: '2px',
                                                                        marginLeft: '2px'
                                                                    }}
                                                                >
                                                                    For Manual Refresh, select 0
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography
                                                                    fontSize='11px'
                                                                    fontWeight={400}
                                                                    marginBottom={'8px'}
                                                                    color="#212121"
                                                                >
                                                                    Default Parameters Override
                                                                </Typography>
                                                                <JsonEditor
                                                                    formik={formik}
                                                                    widgetIndex={index}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </AccordionDetails>
                                                </Accordion>
                                            ))}
                                        </Box>
                                    </Grid>
                                )}


                            </Grid>
                        </form>
                    </div>
                )}
            </Formik>
        </div>
    );
}

function JsonEditor({ formik, widgetIndex }) {
    const defaultParams = useMemo(() => {
        const assignedWidget = formik?.values?.assignedWidgets?.[widgetIndex];
        return assignedWidget?.defaultParams || [];
    }, [formik?.values?.assignedWidgets, widgetIndex]);

    const addParameterPair = () => {
        try {
            const newAssigned = [...formik.values.assignedWidgets];
            const currentParams = newAssigned[widgetIndex].defaultParams || [];
            newAssigned[widgetIndex].defaultParams = [...currentParams, { key: '', value: '', id: uuidv4() }];
            formik.setFieldValue('assignedWidgets', newAssigned);
        } catch (error) {
            showErrorNotification(error.message || "Error in adding parameter pair");
        }
    };

    const removeParameterPair = (id) => {
        try {
            const newAssigned = [...formik.values.assignedWidgets];
            const currentParams = newAssigned[widgetIndex].defaultParams || [];
            if (currentParams.length > 0) {
                newAssigned[widgetIndex].defaultParams = currentParams.filter(pair => pair.id !== id);
                formik.setFieldValue('assignedWidgets', newAssigned);
            }
        } catch (error) {
            showErrorNotification(error.message || "Error in removing parameter pair");
        }
    };

    return (
        <Box sx={{
            border: '1px solid #E6E5E6',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: '#FAFAFA'
        }}>
            <Typography
                sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '14px',
                    color: '#666666',
                    marginBottom: '12px'
                }}
            >
                Override default parameters for this widget instance.
            </Typography>

            <Box sx={{ marginBottom: '16px' }}>
                {defaultParams && Array.isArray(defaultParams) && defaultParams.length > 0 && defaultParams.map((pair, index) => (
                    <Box key={pair.id} sx={{
                        display: 'flex',
                        gap: '12px',
                        marginBottom: '12px',
                        alignItems: 'flex-start'
                    }}>
                        <Box sx={{ flex: 1 }}>
                            <OZInput
                                name={`assignedWidgets.${widgetIndex}.defaultParams.${index}.key`}
                                placeholder="Parameter Key (e.g., width, height)"
                                label="Parameter Key"
                                value={pair.key}
                                size="small"
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <OZInput
                                name={`assignedWidgets.${widgetIndex}.defaultParams.${index}.value`}
                                placeholder="Parameter Value (e.g., 12, true)"
                                label="Parameter Value"
                                value={pair.value}
                                size="small"
                            />
                        </Box>
                        <Button
                            onClick={() => removeParameterPair(pair.id)}
                            sx={{
                                minWidth: '40px',
                                height: '40px',
                                padding: '8px',
                                marginTop: '20px',
                                borderRadius: '8px',
                                border: '1px solid #E6E5E6',
                                backgroundColor: '#FFFFFF',
                                color: '#FF4444',
                                '&:hover': {
                                    backgroundColor: '#FFE6E6',
                                    border: '1px solid #FF4444'
                                }
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </Button>
                    </Box>
                ))}
            </Box>

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addParameterPair}
                fullWidth
                sx={{
                    height: '36px',
                    borderRadius: '8px',
                    border: '1px solid #3D8BF8',
                    backgroundColor: '#FFFFFF',
                    color: '#3D8BF8',
                    fontSize: '12px',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: '#F0F7FF',
                        border: '1px solid #3D8BF8'
                    }
                }}
            >
                Add Parameter
            </Button>
        </Box>
    );
}

export default AssignWidgetsForm;